import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { AppState, AppStateStatus } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useSelector } from 'react-redux';
import getRealtimeChatbotWebsocketUrl from '../requests/Chatbot/GetRealtimeChatbotWebsocketUrl';
import {
  createRealtimeChatbotClient,
  RealtimeChatbotEvent,
} from '../requests/Chatbot/RealtimeChatbotClient';

type InFlightStatus = 'sending' | 'running' | 'cancelling';

export type InFlightMessageState = {
  clientMessageId: string;
  buildingId: string;
  text: string;
  sessionId?: string;
  messageId?: string;
  status: InFlightStatus;
};

type SendRealtimeMessageInput = {
  buildingId: string;
  text: string;
  sessionId?: string;
};

type ConnectionState = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'error';

const IN_FLIGHT_STORAGE_KEY = 'chatbotRealtimeInFlight';
const LAST_SEQ_STORAGE_KEY = 'chatbotRealtimeLastSeq';
const WS_OPEN_STATE = 1;

const generateClientMessageId = () => (
  `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
);

const parseInFlightState = (value: string | null): InFlightMessageState | null => {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    if (!parsed || typeof parsed !== 'object') return null;
    if (!parsed.clientMessageId || !parsed.buildingId || !parsed.text) return null;
    return parsed as InFlightMessageState;
  } catch {
    return null;
  }
};

const parseLastSeq = (value: string | null): number => {
  if (!value) return 0;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

const pickFirstString = (...values: unknown[]) => {
  for (let index = 0; index < values.length; index += 1) {
    const value = values[index];
    if (typeof value === 'string' && value.length > 0) return value;
  }
  return '';
};

const extractStreamText = (event: RealtimeChatbotEvent) => {
  const payload = (event.payload && typeof event.payload === 'object') ? event.payload : {};
  const answer = (event.answer && typeof event.answer === 'object') ? event.answer : {};
  const data = (event.data && typeof event.data === 'object') ? event.data : {};

  return pickFirstString(
    event.content,
    event.delta,
    event.text,
    event.token,
    payload.content,
    payload.delta,
    payload.text,
    payload.token,
    answer.content,
    answer.delta,
    answer.text,
    data.content,
    data.delta,
    data.text,
  );
};

const useRealtimeChatbot = () => {
  const isDev: boolean = useSelector((state:
    { devState: { isDev: boolean } }) => state.devState.isDev);
  const [connectionState, setConnectionState] = useState<ConnectionState>('idle');
  const [inFlightMessage, setInFlightMessage] = useState<InFlightMessageState | null>(null);
  const [progressLabel, setProgressLabel] = useState('');
  const [streamedAssistantText, setStreamedAssistantText] = useState('');
  const [activeMessageId, setActiveMessageId] = useState<string | null>(null);
  const [lastSeq, setLastSeq] = useState(0);
  const [latestEvent, setLatestEvent] = useState<RealtimeChatbotEvent | null>(null);

  const clientRef = useRef<ReturnType<typeof createRealtimeChatbotClient> | null>(null);
  const connectRef = useRef<() => Promise<void>>(async () => {});
  const inFlightMessageRef = useRef<InFlightMessageState | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptRef = useRef(0);
  const lastSeqRef = useRef(0);
  const typingTargetRef = useRef('');
  const typingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const appStateRef = useRef<AppStateStatus>(AppState.currentState);
  const unmountedRef = useRef(false);

  const stopTypingSimulation = useCallback(() => {
    if (!typingIntervalRef.current) return;
    clearInterval(typingIntervalRef.current);
    typingIntervalRef.current = null;
  }, []);

  const ensureTypingSimulation = useCallback(() => {
    if (typingIntervalRef.current) return;

    typingIntervalRef.current = setInterval(() => {
      setStreamedAssistantText((previousText) => {
        const targetText = typingTargetRef.current;

        if (!targetText || previousText === targetText) {
          stopTypingSimulation();
          return previousText;
        }

        const remainingLength = targetText.length - previousText.length;
        const chunkSize = Math.min(4, Math.max(1, Math.ceil(remainingLength / 20)));
        const nextLength = Math.min(targetText.length, previousText.length + chunkSize);
        return targetText.slice(0, nextLength);
      });
    }, 28);
  }, [stopTypingSimulation]);

  const updateTypingTarget = useCallback((nextTarget: string) => {
    if (!nextTarget) return;
    typingTargetRef.current = nextTarget;
    ensureTypingSimulation();
  }, [ensureTypingSimulation]);

  const persistInFlightState = useCallback(async (value: InFlightMessageState | null) => {
    if (!value) {
      await AsyncStorage.removeItem(IN_FLIGHT_STORAGE_KEY);
      return;
    }
    await AsyncStorage.setItem(IN_FLIGHT_STORAGE_KEY, JSON.stringify(value));
  }, []);

  const clearInFlight = useCallback(async () => {
    setInFlightMessage(null);
    setProgressLabel('');
    setStreamedAssistantText('');
    setActiveMessageId(null);
    setLastSeq(0);
    lastSeqRef.current = 0;
    typingTargetRef.current = '';
    stopTypingSimulation();
    await Promise.all([
      AsyncStorage.removeItem(IN_FLIGHT_STORAGE_KEY),
      AsyncStorage.removeItem(LAST_SEQ_STORAGE_KEY),
    ]);
  }, [stopTypingSimulation]);

  const resumeIfNeeded = useCallback(async () => {
    const client = clientRef.current;
    if (!client || client.getReadyState() !== WS_OPEN_STATE) return;

    const persisted = parseInFlightState(await AsyncStorage.getItem(IN_FLIGHT_STORAGE_KEY));
    if (!persisted?.sessionId || !persisted?.messageId) return;

    client.resumeEvents({
      sessionId: persisted.sessionId,
      messageId: persisted.messageId,
      afterSeq: lastSeqRef.current,
    });
  }, []);

  const scheduleReconnect = useCallback(() => {
    if (unmountedRef.current) return;

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    reconnectAttemptRef.current += 1;
    const expDelay = Math.min(30000, 1000 * (2 ** reconnectAttemptRef.current));
    const jitter = Math.floor(Math.random() * 500);
    const delay = expDelay + jitter;

    setConnectionState('reconnecting');
    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectTimeoutRef.current = null;
      connectRef.current().catch(() => undefined);
    }, delay);
  }, []);

  const handleIncomingEvent = useCallback(async (event: RealtimeChatbotEvent) => {
    if (typeof event.seq === 'number') {
      if (event.seq <= lastSeqRef.current) return;
      lastSeqRef.current = event.seq;
      setLastSeq(event.seq);
      await AsyncStorage.setItem(LAST_SEQ_STORAGE_KEY, String(event.seq));
    }

    setLatestEvent(event);

    switch (event.type) {
      case 'connection.ack':
        reconnectAttemptRef.current = 0;
        setConnectionState('connected');
        await resumeIfNeeded();
        break;
      case 'message.accepted': {
        const currentInFlight = inFlightMessageRef.current;
        const nextInFlight: InFlightMessageState = {
          ...(currentInFlight ?? {
            clientMessageId: '',
            buildingId: '',
            text: '',
            status: 'running',
          }),
          sessionId: event.sessionId,
          messageId: event.messageId,
          status: 'running',
        };
        setInFlightMessage(nextInFlight);
        setActiveMessageId(event.messageId ?? null);
        setProgressLabel('Working...');
        await persistInFlightState(nextInFlight);
        break;
      }
      case 'message.running':
      case 'step.started': {
        setProgressLabel(event.label ?? 'Working...');
        if (inFlightMessageRef.current) {
          const nextInFlight = { ...inFlightMessageRef.current, status: 'running' as const };
          setInFlightMessage(nextInFlight);
          await persistInFlightState(nextInFlight);
        }
        break;
      }
      case 'answer.delta': {
        const deltaText = extractStreamText(event);
        if (!deltaText) break;
        updateTypingTarget(`${typingTargetRef.current}${deltaText}`);
        break;
      }
      case 'partial_result': {
        const partialText = extractStreamText(event);
        if (!partialText) break;

        const currentTarget = typingTargetRef.current;
        const nextTarget = partialText.startsWith(currentTarget)
          && partialText.length >= currentTarget.length
          ? partialText
          : `${currentTarget}${partialText}`;

        updateTypingTarget(nextTarget);
        break;
      }
      case 'message.completed': {
        const completionText = extractStreamText(event);
        if (completionText) {
          const currentTarget = typingTargetRef.current;
          const nextTarget = completionText.startsWith(currentTarget)
            && completionText.length >= currentTarget.length
            ? completionText
            : `${currentTarget}${completionText}`;
          updateTypingTarget(nextTarget);
        }
        setProgressLabel('Finalizing response...');
        break;
      }
      case 'message.cancel_requested':
        setProgressLabel('Cancelling...');
        if (inFlightMessageRef.current) {
          const nextInFlight = { ...inFlightMessageRef.current, status: 'cancelling' as const };
          setInFlightMessage(nextInFlight);
          await persistInFlightState(nextInFlight);
        }
        break;
      case 'message.failed':
      case 'message.cancelled':
        await clearInFlight();
        break;
      default:
        break;
    }
  }, [clearInFlight, persistInFlightState, resumeIfNeeded, updateTypingTarget]);

  const connect = useCallback(async () => {
    if (unmountedRef.current) return;

    if (clientRef.current && clientRef.current.getReadyState() === WS_OPEN_STATE) {
      setConnectionState('connected');
      return;
    }

    setConnectionState('connecting');

    try {
      const [clientId, jwt, websocketUrl] = await Promise.all([
        AsyncStorage.getItem('clientName'),
        AsyncStorage.getItem('token'),
        getRealtimeChatbotWebsocketUrl(isDev),
      ]);

      if (!clientId || !jwt) {
        throw new Error('Missing chatbot credentials for realtime connection.');
      }

      if (clientRef.current) {
        clientRef.current.disconnect();
      }

      const client = createRealtimeChatbotClient({ websocketUrl });
      clientRef.current = client;

      client.onEvent((event) => {
        handleIncomingEvent(event).catch(() => undefined);
      });

      client.setOnClose(() => {
        if (unmountedRef.current) return;
        scheduleReconnect();
      });

      client.setOnError(() => {
        if (unmountedRef.current) return;
        setConnectionState('error');
      });

      await client.connect({ clientId, jwt });
      setConnectionState('connected');
      reconnectAttemptRef.current = 0;
      await resumeIfNeeded();
    } catch {
      setConnectionState('error');
      scheduleReconnect();
    }
  }, [handleIncomingEvent, isDev, resumeIfNeeded, scheduleReconnect]);

  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  useEffect(() => {
    inFlightMessageRef.current = inFlightMessage;
  }, [inFlightMessage]);

  const sendMessageRealtime = useCallback(async ({
    buildingId,
    text,
    sessionId,
  }: SendRealtimeMessageInput) => {
    if (!text.trim()) return null;

    if (!clientRef.current || clientRef.current.getReadyState() !== WS_OPEN_STATE) {
      await connect();
    }

    const client = clientRef.current;
    if (!client || client.getReadyState() !== WS_OPEN_STATE) {
      throw new Error('Realtime chatbot websocket is not connected.');
    }

    const clientMessageId = generateClientMessageId();
    const optimisticInFlight: InFlightMessageState = {
      clientMessageId,
      buildingId,
      text,
      ...(sessionId ? { sessionId } : {}),
      status: 'sending',
    };

    typingTargetRef.current = '';
    stopTypingSimulation();
    setInFlightMessage(optimisticInFlight);
    setProgressLabel('Working...');
    setStreamedAssistantText('');
    await persistInFlightState(optimisticInFlight);

    client.sendMessage({
      buildingId,
      text,
      clientMessageId,
      ...(sessionId ? { sessionId } : {}),
    });

    return clientMessageId;
  }, [connect, persistInFlightState, stopTypingSimulation]);

  const cancelMessageRealtime = useCallback(async () => {
    const client = clientRef.current;
    if (!client || client.getReadyState() !== WS_OPEN_STATE) return;
    if (!inFlightMessage?.sessionId || !inFlightMessage?.messageId) return;

    setProgressLabel('Cancelling...');
    const cancellingState = {
      ...inFlightMessage,
      status: 'cancelling' as const,
    };
    setInFlightMessage(cancellingState);
    await persistInFlightState(cancellingState);

    client.cancelMessage({
      sessionId: inFlightMessage.sessionId,
      messageId: inFlightMessage.messageId,
    });
  }, [inFlightMessage, persistInFlightState]);

  useEffect(() => {
    unmountedRef.current = false;
    connect().catch(() => undefined);

    const appStateSubscription = AppState.addEventListener('change', (nextState) => {
      const wasBackgrounded = appStateRef.current !== 'active';
      appStateRef.current = nextState;

      if (nextState === 'active' && wasBackgrounded) {
        connect().catch(() => undefined);
      }
    });

    return () => {
      unmountedRef.current = true;
      appStateSubscription.remove();

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }

      if (clientRef.current) {
        clientRef.current.disconnect();
        clientRef.current = null;
      }

      stopTypingSimulation();
    };
  }, [connect, stopTypingSimulation]);

  useEffect(() => {
    const hydrateInFlight = async () => {
      const [persistedInFlight, persistedSeq] = await Promise.all([
        AsyncStorage.getItem(IN_FLIGHT_STORAGE_KEY),
        AsyncStorage.getItem(LAST_SEQ_STORAGE_KEY),
      ]);

      const persisted = parseInFlightState(persistedInFlight);
      const seq = parseLastSeq(persistedSeq);

      lastSeqRef.current = seq;
      setLastSeq(seq);

      if (!persisted) return;

      if (!persisted.sessionId || !persisted.messageId) {
        await Promise.all([
          AsyncStorage.removeItem(IN_FLIGHT_STORAGE_KEY),
          AsyncStorage.removeItem(LAST_SEQ_STORAGE_KEY),
        ]);
        return;
      }

      setInFlightMessage(persisted);
      setActiveMessageId(persisted.messageId ?? null);
      setProgressLabel(persisted.status === 'cancelling' ? 'Cancelling...' : 'Resuming...');
    };

    hydrateInFlight().catch(() => undefined);
  }, []);

  return {
    connectionState,
    inFlightMessage,
    progressLabel,
    streamedAssistantText,
    activeMessageId,
    lastSeq,
    latestEvent,
    sendMessageRealtime,
    cancelMessageRealtime,
    clearInFlight,
    resumeIfNeeded,
    connect,
  };
};

export default useRealtimeChatbot;
